import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { cn } from '../../common/utils';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import * as Icon from '../../components/icons';
import { Container } from '../../components/layout/ContainerFlex';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import TitleSection from '../../components/layout/TitleSection';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import SidebarContainer, {
  SidebarContainerProps,
} from '../../containers/SidebarContainer';
import FilterPresets from '../../filterPreset/FilterPresets';
import FilterPresetSelector from '../../filterPreset/FilterPresetSelector';
import useSimulationHqMetricDescriptorsMap from '../../metrics/simulationHq/useSimulationHqMetricDescriptors';
import { namedColorsGroupSelectedId } from '../../store/namedColors.state';
import { sidebarStateByType } from '../../store/sidebar.state';
import useSimulationPresentationWatcher from '../hooks/useSimulationPresentationWatcher';
import SimulationAssignmentSelect from '../SimulationAssignmentSelect';
import { simulationHqMetricSelected } from '../store/simulation.hq.heatmap.state';
import ActionBarNextStep from './ActionBarNextStep';

const DatasetStatsSidebar: React.FC = () => {
  const { t } = useTranslation('simulation');
  const [metric, setMetric] = useRecoilState(simulationHqMetricSelected);
  const metricDescriptors = useSimulationHqMetricDescriptorsMap();
  const [showPresets, setShowPresets] = useState(false);
  const setNamedColorGroup = useSetRecoilState(namedColorsGroupSelectedId);
  const setFilterEditorPanelState = useSetRecoilState(
    sidebarStateByType('sidebar-filter-preset-editor'),
  );
  useEffect(() => {
    setNamedColorGroup('policy');
  }, []);

  useEffect(() => {
    setFilterEditorPanelState({
      isHidden: !showPresets,
    });
  }, [showPresets]);

  // watcher for autosave filter presets
  useSimulationPresentationWatcher();

  const grouped = _.groupBy(metricDescriptors, d => d.titleCategory);
  return (
    <>
      <div className="flex p-2">
        <div className="flex flex-1">
          <FilterPresetSelector />
        </div>
        <ButtonSwitchMulti
          autoSize
          title=""
          classNameContainer="flex-none"
          className={cn('mx-0.5 xl:mx-3')}
          classNameLabel={cn('text-xs py-0.5 px-2')}
          buttonType="header"
          options={[{ label: t`kpi` }, { label: t`setup` }]}
          // disabled={allocationRun?.status !== BatchJobStatus.READY}
          selectedIndex={showPresets ? 1 : 0}
          onClick={index => setShowPresets(index === 1)}
        />
      </div>

      {!showPresets && (
        <Container hasOverflowY col>
          <ScreenTitle
            title={t`Heatmaps`}
            subtitle={t`Analyse`}
            isSticky
            helpNavTo={'actuality/feed-stats'}
            icon={Icon.HeatMap9}
          />
          <TitleSection
            inPanelView
            title={t`Heatmap by`}
            classNameInner="pb-20 mb-4 flex-1"
          >
            <StatGroup>
              {_.map(grouped, (metrics, groupTitle) => {
                return (
                  <TitleSection
                    key={`dataset-metric-group-${groupTitle}`}
                    id={`dataset-metric-group-${groupTitle}`}
                    title={groupTitle}
                    //inSidebarView
                    inPanelView
                    collapsible
                    classNameInner="space-y-px"
                  >
                    {_.map(metrics, d => {
                      return (
                        <Stat
                          key={d.title}
                          isActionable
                          isSelectable
                          isSelected={metric === d.type}
                          title={d.title}
                          onClick={() => {
                            setMetric(d.type);
                          }}
                        />
                      );
                    })}
                  </TitleSection>
                );
              })}
            </StatGroup>
          </TitleSection>
        </Container>
      )}
      {showPresets && <FilterPresets />}
      <ActionBarNextStep />
    </>
  );
};

export default () => {
  const { t } = useTranslation('simulation');

  const containerProps: SidebarContainerProps = {
    type: 'sidebar-dataset-stats',
    title: t`Heatmaps`,
    titleAction: (
      <>
        <span>{t`Heatmaps`}</span>
        <SimulationAssignmentSelect />
      </>
    ),
    loadingMessage: t`Loading Data...`,
    size: 'xs',
  };

  return (
    <SidebarContainer key={containerProps.type} {...containerProps}>
      <DatasetStatsSidebar />
    </SidebarContainer>
  );
};
